import { prepareOpenAIEventId, emitOpenAIEvent } from './openai-measurement'
/**
 * Analytics module - GA4-style events routed to RudderStack
 * 
 * Provides typed, sanitized event logging with global context support
 */

import React from 'react'
import { EventName, EventParams, GlobalParams } from './events'
import useRudderStackAnalytics from '../useRudderAnalytics'
import { enqueueEvent, flushEventsBeacon } from './eventQueue'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import { errorLog, debugLog } from '../utils/log-helper'
import { prepareWebRestaurantDecision, emitWebRestaurantDecision } from './web-restaurant-decision'

/**
 * Native app version (iOS CFBundleShortVersionString / Android versionName, plus build number),
 * resolved once via Capacitor. App.getInfo() is async and native-only (throws on web), so we cache
 * it at module load and read it synchronously in getGlobalContext. This reflects the ACTUAL installed
 * store version — the right cohort key for the mobile audience. Web falls back to the build-time
 * REACT_APP_VERSION (CI short SHA) when present.
 */
let nativeAppVersion: string | undefined
if (Capacitor.isNativePlatform()) {
  void CapApp.getInfo()
    .then((info) => {
      nativeAppVersion = info.build ? `${info.version} (${info.build})` : info.version
    })
    .catch((error) => {
      errorLog('[Analytics] Failed to read native app version', error)
    })
}

/**
 * PII keys that should never be logged (all lowercase for case-insensitive matching)
 */
const PII_KEYS = new Set(['email', 'phone', 'name', 'firstname', 'lastname', 'fullname'])

/**
 * Max string length for event names and param values
 */
const MAX_EVENT_NAME_LENGTH = 40
const MAX_PARAM_VALUE_LENGTH = 100

/**
 * DEBUG_ANALYTICS flag - enable console logging for all events
 * Set via localStorage: localStorage.setItem('DEBUG_ANALYTICS', 'true')
 * Or via URL param: ?debug_analytics=1
 */
function isDebugAnalyticsEnabled(): boolean {
  if (typeof window === 'undefined') return false
  
  // Check URL param
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('debug_analytics') === '1') return true
  
  // Check localStorage
  try {
    return localStorage.getItem('DEBUG_ANALYTICS') === 'true'
  } catch {
    return false
  }
}

/**
 * Timing storage for duration tracking
 */
const timingStorage = new Map<string, number>()

/**
 * Global context providers
 */
const globalContextProviders: Array<() => Partial<GlobalParams>> = []

/**
 * Sanitize event parameters
 * - Remove undefined/null values
 * - Block PII keys
 * - Truncate overly long strings
 * 
 * @internal - Exported for testing only
 */
export function sanitizeParams(params: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {}

  for (const [key, value] of Object.entries(params)) {
    // Skip undefined/null
    if (value === undefined || value === null) {
      continue
    }

    // Block PII keys
    if (PII_KEYS.has(key.toLowerCase())) {
      if (process.env.NODE_ENV !== 'production') {
        errorLog(`[Analytics] Blocked PII key: ${key}`)
      }
      continue
    }

    // Truncate strings
    if (typeof value === 'string' && value.length > MAX_PARAM_VALUE_LENGTH) {
      sanitized[key] = value.substring(0, MAX_PARAM_VALUE_LENGTH)
      if (process.env.NODE_ENV !== 'production') {
        errorLog(`[Analytics] Truncated param ${key} from ${value.length} to ${MAX_PARAM_VALUE_LENGTH} chars`)
      }
    } else {
      sanitized[key] = value
    }
  }

  return sanitized
}

/**
 * Get current global context from all providers
 * 
 * @internal - Exported for testing only
 */
export function getGlobalContext(): Partial<GlobalParams> {
  const context: Partial<GlobalParams> = {}

  // Add platform
  const platform = Capacitor.getPlatform()
  if (platform === 'ios' || platform === 'android' || platform === 'web') {
    context.platform = platform
  }

  // Add environment if available
  const env = process.env.NODE_ENV
  if (env === 'development') {
    context.environment = 'local'
  } else if (env === 'production') {
    context.environment = 'prod'
  } else if (env === 'test') {
    context.environment = 'dev'
  }

  // Add app version: native store version (iOS/Android) when available, else the web build id.
  const appVersion = nativeAppVersion ?? process.env.REACT_APP_VERSION
  if (appVersion) {
    context.app_version = appVersion
  }

  // Merge context from providers
  for (const provider of globalContextProviders) {
    try {
      Object.assign(context, provider())
    } catch (error) {
      errorLog('[Analytics] Error getting global context from provider', error)
    }
  }

  return context
}

/**
 * Validate event name length
 */
function validateEventName(name: string): void {
  if (name.length > MAX_EVENT_NAME_LENGTH) {
    throw new Error(`Event name "${name}" exceeds max length of ${MAX_EVENT_NAME_LENGTH} characters`)
  }
}

/**
 * GA4 (gtag) fan-out flag. Off by default — the first-party events pipeline (enqueueEvent) is the
 * always-on source of truth; gtag is optional and only fires when explicitly enabled.
 */
function isGa4Enabled(): boolean {
  return process.env.REACT_APP_GA4_ENABLED === 'true'
}

/**
 * Log an analytics event
 * 
 * @param name - Event name (must be <= 40 chars, lowercase + underscores)
 * @param params - Event-specific parameters (will be merged with global context)
 */
export function logEvent<T extends EventName>(
  name: T,
  params?: Partial<EventParams<T>>,
): void {
  // Validate event name (throw before try-catch so validation errors propagate)
  validateEventName(name)
  
  try {

    // Get global context
    const globalContext = getGlobalContext()

    // Merge params with global context
    const mergedParams = {
      ...globalContext,
      ...params,
    }

    // Sanitize params
    const sanitized = sanitizeParams(mergedParams)

    // DEBUG_ANALYTICS: Log to console if enabled
    if (isDebugAnalyticsEnabled()) {
      debugLog('[DEBUG_ANALYTICS] Event:', name, 'Payload:', sanitized)
    }

    // First-party events pipeline -> POST /events (BigQuery). Always-on source of truth.
    // Fire-and-forget.
    const webDecision = prepareWebRestaurantDecision(name, sanitized)
    const openAIEventId = prepareOpenAIEventId(name, webDecision?.decision_event_id)
    enqueueEvent(name, {
      ...sanitized,
      ...(webDecision ? { decision_event_id: webDecision.decision_event_id } : {}),
      ...(openAIEventId ? { openai_event_id: openAIEventId } : {}),
    })
    if (openAIEventId) emitOpenAIEvent(name, openAIEventId, !!webDecision)

    // Narrow web terminal signal, independent of the optional broad GA4 fan-out below.
    // Retain the original event and a shared ID for exact first-party/GA4 reconciliation.
    if (webDecision) emitWebRestaurantDecision(webDecision)

    // Optional GA4 fan-out (off by default; enable with REACT_APP_GA4_ENABLED=true).
    if (isGa4Enabled() && typeof window !== 'undefined' && (window as any).gtag) {
      ;(window as any).gtag('event', name, sanitized)
    }

    // Log to debug in dev
    if (process.env.NODE_ENV !== 'production') {
      debugLog('[Analytics]', name, sanitized)
    }
  } catch (error) {
    errorLog('[Analytics] Error logging event', error)
  }
}

/**
 * Synchronously hand any queued events to a keepalive/beacon transport *before* a full-page
 * navigation (e.g. an Auth0 login redirect). Events logged immediately before such a navigation —
 * like `login_started` — are otherwise lost when the document is torn down and the normal async POST
 * is cancelled, which broke the login→signup funnel (account_created out-ran login_started). Call
 * this right after logging the pre-redirect event. Never throws — analytics must not block auth.
 */
export function flushEventsBeforeNavigation(): void {
  try {
    flushEventsBeacon()
  } catch {
    // best-effort: never block navigation on analytics
  }
}

/**
 * Set user ID (hashed/opaque, no PII)
 */
export function setUserId(userId: string | null): void {
  try {
    if (!userId) return

    // Optional GA4 fan-out only (off by default). First-party identity stitching happens
    // server-side via the never-rotated anon id; RudderStack fan-out has been removed.
    if (isGa4Enabled() && typeof window !== 'undefined' && (window as any).gtag) {
      ;(window as any).gtag('set', { user_id: userId })
    }
  } catch (error) {
    errorLog('[Analytics] Error setting user ID', error)
  }
}

/**
 * Set user properties (e.g., tier, subscription status)
 */
export function setUserProperties(props: { tier?: string; [key: string]: any }): void {
  try {
    // Optional GA4 fan-out only (off by default). RudderStack fan-out has been removed.
    if (isGa4Enabled() && typeof window !== 'undefined' && (window as any).gtag) {
      ;(window as any).gtag('set', 'user_properties', props)
    }
  } catch (error) {
    errorLog('[Analytics] Error setting user properties', error)
  }
}

/**
 * Start timing for duration tracking
 * 
 * @param key - Unique key for this timing operation
 */
export function timeStart(key: string): void {
  timingStorage.set(key, Date.now())
}

/**
 * End timing and return duration in milliseconds
 * 
 * @param key - Key used in timeStart
 * @returns Duration in milliseconds, or 0 if key not found
 */
export function timeEnd(key: string): number {
  const startTime = timingStorage.get(key)
  if (startTime === undefined) {
    if (process.env.NODE_ENV !== 'production') {
      errorLog(`[Analytics] timeEnd called for unknown key: ${key}`)
    }
    return 0
  }
  
  timingStorage.delete(key)
  return Date.now() - startTime
}

/**
 * Add a global context provider
 * 
 * @param getContext - Function that returns partial global params
 */
export function addGlobalContext(getContext: () => Partial<GlobalParams>): void {
  globalContextProviders.push(getContext)
}

/**
 * Hook to initialize analytics and set RudderStack instance on window
 * Call this in App.tsx or a top-level component
 * 
 * Note: This is now handled by the singleton in rudderClient.ts,
 * but we keep this for backward compatibility and window assignment
 */
export function useAnalytics(): void {
  const [analytics, isReady] = useRudderStackAnalytics()
  
  React.useEffect(() => {
    if (analytics && isReady && typeof window !== 'undefined') {
      ;(window as any).__rudderstackAnalytics = analytics
    }
  }, [analytics, isReady])
}
